import { Alert, Button, Card, CardBody, Input, Textarea } from '@foundation/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import styles from './collection-pages.new.module.css'
import { api } from '@/lib/api'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/collection-pages/new')({
  component: CollectionPagesNew,
})

function toKebabCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

function CollectionPagesNew() {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [slugTouched, setSlugTouched] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    headline: '',
    description: '',
    brandColor: '#6366f1',
    thankYouMessage: 'Thank you for your testimonial!',
  })

  const handleNameChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      name: value,
      slug: slugTouched ? prev.slug : toKebabCase(value),
    }))
  }

  const handleSlugChange = (value: string) => {
    setSlugTouched(true)
    setFormData((prev) => ({ ...prev, slug: toKebabCase(value) }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }
    if (!formData.headline.trim()) {
      setError('Headline is required')
      return
    }

    try {
      setIsSubmitting(true)
      const request: Parameters<typeof api.createCollectionPage>[0] = {
        name: formData.name.trim(),
        headline: formData.headline.trim(),
        brandColor: formData.brandColor,
      }
      if (formData.slug) request.slug = formData.slug
      if (formData.description.trim()) request.description = formData.description.trim()
      if (formData.thankYouMessage.trim()) request.thankYouMessage = formData.thankYouMessage.trim()
      const response = await api.createCollectionPage(request)
      toast.success('Collection page created successfully')
      void navigate({ to: '/collection-pages/$id', params: { id: response.data.id } })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create collection page')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={styles.container}>
      <p className={styles.description}>
        Create a page where your customers can submit testimonials. Share the link via email, social
        media, or embed it on your website.
      </p>

      {error && (
        <Alert variant="danger" style={{ marginBottom: '1rem' }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardBody>
          <form className={styles.form} onSubmit={(e) => void handleSubmit(e)}>
            <Input
              label="Page Name"
              type="text"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g., Customer Feedback"
              required
            />

            <Input
              label="Slug"
              type="text"
              value={formData.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder="e.g., customer-feedback"
            />
            {formData.slug && (
              <p className={styles.slugPreview}>
                URL: <span>/collect/{formData.slug}</span>
              </p>
            )}

            <Input
              label="Headline"
              type="text"
              value={formData.headline}
              onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
              placeholder="e.g., We'd love your feedback!"
              required
            />

            <Textarea
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Tell your customers what kind of feedback you're looking for..."
              rows={3}
            />
            <p className={styles.fieldHint}>
              Optional. Shown below the headline on the collection page.
            </p>

            <div className={styles.colorField}>
              <span className={styles.colorLabel}>Brand Color</span>
              <div className={styles.colorInput}>
                <input
                  type="color"
                  className={styles.colorSwatch}
                  value={formData.brandColor}
                  onChange={(e) => setFormData({ ...formData, brandColor: e.target.value })}
                />
                <span className={styles.colorValue}>{formData.brandColor}</span>
              </div>
            </div>

            <Input
              label="Thank You Message"
              type="text"
              value={formData.thankYouMessage}
              onChange={(e) => setFormData({ ...formData, thankYouMessage: e.target.value })}
              placeholder="Message shown after submission"
            />
            <p className={styles.fieldHint}>Shown to customers after they submit a testimonial.</p>

            <div className={styles.formActions}>
              <Button type="submit" variant="primary" loading={isSubmitting}>
                Create Collection Page
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => void navigate({ to: '/collection-pages' })}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  )
}
